/* oxlint-disable react-hooks/refs -- Floating UI exposes callback refs through its refs object. */
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useRef } from "react";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import type { LanguageDto } from "@/api/types";
import { useLogout } from "@/authentication/useLogout";
import { Announcement03Icon } from "@/components/Icons/Announcement03Icon";
import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { FileCheck02Icon } from "@/components/Icons/FileCheck02Icon";
import { FileShield02Icon } from "@/components/Icons/FileShield02Icon";
import { LogOut01Icon } from "@/components/Icons/LogOut01Icon";
import { Shield01Icon } from "@/components/Icons/Shield01Icon";
import { UserEditIcon } from "@/components/Icons/UserEditIcon";
import { UserPlus01Icon } from "@/components/Icons/UserPlus01Icon";
import { ProductUpdateUnreadDot } from "@/components/ProductUpdates/ProductUpdatesDrawer";
import { useProductUpdates } from "@/components/ProductUpdates/ProductUpdatesProvider";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { buttonVariants } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { getFlagIcon } from "@/helpers/flags";
import { mergeRefs } from "@/helpers/mergeRefs";
import { twResolve } from "@/helpers/tw-resolve";
import { useSessionUserOptional } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useSidebarManager } from "@/hooks/useSidebar";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { routes } from "@/routes";
import type { Languages } from "@/translations";
import { useTranslation } from "@/translations";
import { RuntimeFloatingPortal, useRuntimeFloatingPanel } from "@/ui-adapters/RuntimeFloatingPanel";

import type { IconComponent } from "../Icon/Icon";
import { Bell01Icon } from "../Icons/Bell01Icon";
import { Globe02Icon } from "../Icons/Globe02Icon";
import { ResidentUserMenuModals } from "./ResidentUserMenuModals";

interface NavigationSidebarUserMenuProps {
  isPortfolio?: boolean;
}

export function NavigationSidebarUserMenu({ isPortfolio }: NavigationSidebarUserMenuProps): React.ReactNode {
  const [isMenuOpen, menuOpenHandlers] = useBool(false);
  const [isPrivacySettingsOpen, privacySettingsOpenHandlers] = useBool(false);
  const [isHousemateInviteOpen, housemateInviteOpenHandlers] = useBool(false);

  const logout = useLogout();
  const { t } = useTranslation();
  const [sessionUser] = useSessionUserOptional();
  const isMonthlyLivingExperienceReportEmailEnabled = useConfig("isMonthlyLivingExperienceReportEmailEnabled");
  const { isCollapsed } = useSidebarManager();
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuPanelRef = useRef<HTMLElement | null>(null);
  const slug = useSlug({
    optional: true,
  });
  const postHog = usePostHog();
  const placement = isCollapsed ? "right-end" : "top";
  const { referenceRef, panelProps } = useRuntimeFloatingPanel({
    open: isMenuOpen,
    placement,
  });
  const refs = { setReference: referenceRef };
  const updateSelfLanguageMutation = selfAccountMutations.useUpdateLanguage();
  const { hasUnreadUpdates, openProductUpdates } = useProductUpdates();
  const isCompanyBased = sessionUser?.project.type === "companyBased";
  const canInviteResident = isCompanyBased ? sessionUser?.company !== undefined : sessionUser?.address !== undefined;

  useClickOutside([userMenuRef, userMenuPanelRef], menuOpenHandlers.setFalse, isMenuOpen);

  const handleChangeLanguage = async (uiLanguageId: Languages) => {
    await updateSelfLanguageMutation.mutateAsync({
      uiLanguageId: uiLanguageId,
      autoTranslationLanguageId: sessionUser?.autoTranslationLanguage?.id,
    });
  };

  return (
    <>
      <section className={twJoin("relative flex justify-center", isCollapsed ? "w-fit" : "w-full")} ref={userMenuRef}>
        <section className="w-full rounded-lg border border-border-default bg-background-default">
          <button
            type="button"
            className={twResolve(
              buttonVariants({ variant: "subtle", size: "md", fullWidth: true }),
              "relative z-0 h-control-xl min-h-control-md flex-row justify-between gap-space-sm overflow-visible rounded-lg whitespace-normal transition-colors focus-visible:bg-action-subtle-primary-hover hocus:bg-action-subtle-primary-hover",
              isCollapsed ? "p-space-xs" : "p-space-sm",
            )}
            aria-expanded={isMenuOpen}
            aria-haspopup="dialog"
            aria-label={t("component.user-menu.menu")}
            data-testid="user-menu-button"
            onClick={menuOpenHandlers.toggle}
            ref={refs.setReference}
          >
            <Inline gap="sm" align="center">
              <span className="relative">
                <UserAvatar size="sm" image={sessionUser?.avatar} />
                {hasUnreadUpdates && (
                  <span className="absolute -top-0.5 -right-0.5">
                    <ProductUpdateUnreadDot />
                  </span>
                )}
              </span>
              {!isCollapsed && (
                <span className="line-clamp-1 text-left text-body break-all">
                  {sessionUser?.fullName || <Spinner size="sm" reveal="delayed" />}
                </span>
              )}
            </Inline>
            {!isCollapsed && (
              <span className={twJoin("transition-transform", isMenuOpen ? "" : "rotate-180")}>
                <ChevronDownIcon />
              </span>
            )}
            {isMenuOpen && isCollapsed && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-12 line-clamp-1 h-full w-navigation-user-menu-label text-left"
              >
                {sessionUser?.fullName || "..."}
              </span>
            )}
          </button>
        </section>
      </section>
      {isMenuOpen && (
        <RuntimeFloatingPortal>
          <aside
            aria-label={t("component.user-menu.menu")}
            className={twJoin(
              "z-modal flex h-fit flex-col gap-space-sm rounded-lg border border-border-default bg-background-default shadow-raised",
              "max-h-screen-minus-16 overflow-y-auto",
              placement === "top" && "-top-space-sm! w-navigation-user-menu-panel-compact",
              placement === "right-end" && "left-space-sm! w-navigation-user-menu-panel-wide",
            )}
            role="dialog"
            {...panelProps}
            ref={mergeRefs(panelProps.ref, userMenuPanelRef)}
          >
            {!isPortfolio && (
              <LanguageSelector
                selectedLanguage={sessionUser?.language?.id || "en"}
                onSelectLanguage={handleChangeLanguage}
              />
            )}
            {!sessionUser?.role.isResidentType && (
              <UserMenuItem
                icon={<Announcement03Icon />}
                label={t("component.user-menu.whats-new")}
                onClick={() => {
                  menuOpenHandlers.setFalse();
                  openProductUpdates();
                }}
                indicator={hasUnreadUpdates ? <ProductUpdateUnreadDot /> : undefined}
                data-testid="user-menu-product-updates"
              />
            )}
            {!isPortfolio && slug && sessionUser && (
              <UserMenuItem
                data-testid="user-menu-self-edit"
                href={
                  sessionUser.role.isResidentType
                    ? routes.users.editSelf({
                        slug,
                      })
                    : routes.users.edit({
                        slug,
                        id: sessionUser.id,
                      })
                }
                onClick={() => {
                  menuOpenHandlers.setFalse();
                  postHog.reset(true);
                }}
                icon={<UserEditIcon />}
                label={t("component.user-menu.user-settings")}
              />
            )}
            {!isPortfolio && sessionUser?.role.isResidentType && (
              <ResidentUserMenuItems
                canInviteResident={canInviteResident}
                isCompanyBased={isCompanyBased}
                onCloseMenu={menuOpenHandlers.setFalse}
                onOpenPrivacySettings={privacySettingsOpenHandlers.setTrue}
                onOpenHousemateInvite={housemateInviteOpenHandlers.setTrue}
              />
            )}
            {!isPortfolio && slug && sessionUser?.isAdmin && (
              <UserMenuItem
                icon={<Bell01Icon />}
                onClick={menuOpenHandlers.setFalse}
                label={t("component.user-menu.admin-notification-settings")}
                href={routes.adminNotifications.selfSettings({ slug })}
              />
            )}
            {isMonthlyLivingExperienceReportEmailEnabled && isPortfolio && slug && sessionUser?.isAdmin && (
              <UserMenuItem
                icon={<Bell01Icon />}
                onClick={menuOpenHandlers.setFalse}
                label={t("component.user-menu.account-notification-settings")}
                href={routes.account.notificationSettings()}
              />
            )}

            <UserMenuItem
              icon={<LogOut01Icon />}
              label={t("component.user-menu.log-out")}
              onClick={logout}
              data-testid="user-menu-logout"
            />
          </aside>
        </RuntimeFloatingPortal>
      )}
      {!isPortfolio && sessionUser?.role.isResidentType && (
        <ResidentUserMenuModals
          sessionUser={sessionUser}
          isPrivacySettingsOpen={isPrivacySettingsOpen}
          isHousemateInviteOpen={isHousemateInviteOpen}
          onPrivacySettingsOpenChange={privacySettingsOpenHandlers.set}
          onHousemateInviteOpenChange={housemateInviteOpenHandlers.set}
        />
      )}
    </>
  );
}

type ResidentUserMenuItemsProps = {
  canInviteResident: boolean;
  isCompanyBased: boolean;
  onCloseMenu: () => void;
  onOpenPrivacySettings: () => void;
  onOpenHousemateInvite: () => void;
};

function ResidentUserMenuItems({
  canInviteResident,
  isCompanyBased,
  onCloseMenu,
  onOpenPrivacySettings,
  onOpenHousemateInvite,
}: ResidentUserMenuItemsProps): React.ReactNode {
  const { t } = useTranslation();
  const { appBrandingConfig } = useProjectAppBrandingConfig();

  return (
    <>
      <UserMenuItem
        icon={<Shield01Icon />}
        label={t("component.user-menu.privacy-settings")}
        onClick={() => {
          onCloseMenu();
          onOpenPrivacySettings();
        }}
        data-testid="user-menu-privacy-settings"
      />
      {canInviteResident && (
        <UserMenuItem
          icon={<UserPlus01Icon />}
          label={
            isCompanyBased ? t("view.menu.share_code.company.header.title") : t("component.user-menu.invite-housemate")
          }
          onClick={() => {
            onCloseMenu();
            onOpenHousemateInvite();
          }}
          data-testid="user-menu-invite-housemate"
        />
      )}
      <UserMenuItem
        icon={<FileCheck02Icon />}
        label={t("component.user-menu.license-agreement")}
        href={appBrandingConfig.licenseAgreementLink}
        isExternal
        onClick={onCloseMenu}
      />
      <UserMenuItem
        icon={<FileShield02Icon />}
        label={t("component.user-menu.privacy-statement")}
        href={appBrandingConfig.privacyPolicyLink}
        isExternal
        onClick={onCloseMenu}
      />
    </>
  );
}

type UserMenuItemProps = {
  icon: IconComponent;
  label: string;
  "data-testid"?: string;
  href?: string;
  isExternal?: boolean;
  onClick?: () => void;
  indicator?: React.ReactNode;
};

function UserMenuItem({
  icon,
  label,
  onClick,
  href,
  isExternal,
  indicator,
  "data-testid": dataTestId,
}: UserMenuItemProps) {
  const Component = href ? Link : "button";
  const content = (
    <>
      <span className="p-space-sm">{icon}</span>
      <span className="truncate">{label}</span>
      {indicator && <span className="ml-auto px-space-sm">{indicator}</span>}
    </>
  );

  const className =
    "flex w-full flex-row items-center gap-space-sm p-space-sm outline-hidden focus-visible:bg-background-brand-light hocus:bg-background-brand-light";

  return (
    <Component
      className={className}
      onClick={onClick}
      data-testid={dataTestId}
      to={href || ""}
      target={isExternal && href && !href.startsWith("mailto:") ? "_blank" : undefined}
      rel={isExternal && href && !href.startsWith("mailto:") ? "noopener noreferrer" : undefined}
      type={href ? undefined : "button"}
    >
      {content}
    </Component>
  );
}

type LanguageSelectorProps = {
  selectedLanguage: LanguageDto["id"];
  onSelectLanguage: (uiLanguageId: LanguageDto["id"]) => void;
};

function LanguageSelector({ selectedLanguage, onSelectLanguage }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const generalQueries = useGeneralQueries();
  const { data: uiLanguages = [] } = useQuery(generalQueries.languages({ subset: "ui" }));

  return (
    <section className="flex w-full items-center gap-space-sm p-space-sm pb-space-xs">
      <label htmlFor="language" hidden>
        {t("component.user-menu.language")}
      </label>
      <span className="p-space-sm">
        <Globe02Icon />
      </span>
      <div className="flex-1">
        <Select
          id="language"
          items={uiLanguages}
          keySelector={(x) => x.id}
          renderOption={(x) => {
            const icon = getFlagIcon({
              languageId: x.id,
              width: 16,
            });

            return (
              <Inline gap="sm" align="center">
                {icon || <span className="aspect-4/3 w-icon-inline bg-background-secondary" />}
                <span>{x.description}</span>
              </Inline>
            );
          }}
          placement="top-left"
          selected={uiLanguages.find((currLanguage) => currLanguage.id === selectedLanguage) || uiLanguages[0]}
          onChange={(item) => onSelectLanguage(item.id)}
        />
      </div>
    </section>
  );
}
